import { useAuth } from '@clerk/react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useUserSettingsOptional } from '@/components/user-settings-context'
import { useDevice } from '@/hooks/use-mobile'
import { CodeFooter } from './code-pane/code-footer'
import { CodeToolbar } from './code-pane/code-toolbar'
import { MonacoLoader } from './code-pane/monaco-loader'
import { useCodeRefOptional } from './code-ref-context'
import { EditorActionsProvider } from './editor-actions'
import type { LanguageValue } from './editor-languages'
import { useMonacoEditor } from './hooks/use-monaco-editor'
import { useSessionOptional } from './session-context'
import { useTestCasesOptional } from './test-cases-context'

const DEFAULT_FONT_FAMILY = 'Menlo, Monaco, Consolas, "Courier New", monospace'

function resolveFontFamily(font: string): string {
  if (font === 'fira-code') return `"Fira Code", ${DEFAULT_FONT_FAMILY}`
  return DEFAULT_FONT_FAMILY
}

export function CodeEditorPane() {
  const { isSignedIn } = useAuth()
  const {
    MonacoComponent,
    isClient,
    isDarkMode,
    language,
    setLanguage,
    code,
    setCode,
    cursorPosition,
    handleFormat,
    handleReset,
    handleRun,
    handleRunRaw,
    handleSubmit,
    isExecuting,
    isSubmitting,
    isFormatting,
    editorRef,
    handleDidMount,
    handleChange,
    vimInitFailed,
  } = useMonacoEditor()

  // Keep shared codeRef in sync for AI pane access
  const codeRef = useCodeRefOptional()
  const handleChangeWithRef = useCallback(
    (value: string | undefined) => {
      if (typeof value === 'string' && codeRef) {
        codeRef.current = value
      }
      handleChange(value)
    },
    [handleChange, codeRef],
  )

  // Sync codeRef on programmatic code changes (reset, language switch, draft load)
  useEffect(() => {
    if (codeRef) codeRef.current = code
  }, [code, codeRef])

  // Get test cases from shared context (discriminated union)
  const testCasesContext = useTestCasesOptional()
  const testCasesState = testCasesContext?.state

  // Bridge handleReset up to SessionContext so SessionLifecycleModals (rendered
  // above EditorActionsProvider) can drive a Start Fresh editor reset.
  const sessionContext = useSessionOptional()
  const registerEditorSetCodeCallback =
    sessionContext?.registerEditorSetCodeCallback
  useEffect(() => {
    sessionContext?.registerEditorResetCallback(handleReset)
    return () => {
      sessionContext?.registerEditorResetCallback(null)
    }
  }, [sessionContext, handleReset])

  // Read editor settings from user settings context
  const { settings } = useUserSettingsOptional()
  const editorSettings = settings.editor
  const shortcutSettings = settings.shortcuts
  const { isMobile } = useDevice()

  // Store refs to run/submit handlers and shortcut settings for keyboard shortcuts.
  // Using refs ensures the Monaco keybindings (registered once at mount) always
  // read the latest handler versions and respect toggled-off shortcuts.
  const runHandlerRef = useRef<() => void>(() => {})
  const submitHandlerRef = useRef<() => void>(() => {})
  const isSignedInRef = useRef(isSignedIn)
  const shortcutSettingsRef = useRef(shortcutSettings)

  // Wrap handleDidMount to register keyboard shortcuts when editor mounts.
  // Shortcuts are always registered (onMount fires only once), and the auth
  // check happens at invocation time via isSignedInRef so that users who
  // sign in after the editor loads still get working keybindings.
  const handleDidMountWithShortcuts = useCallback(
    (
      editorInstance: Parameters<typeof handleDidMount>[0],
      monaco: Parameters<typeof handleDidMount>[1],
    ) => {
      // Call the original mount handler first
      handleDidMount(editorInstance, monaco)

      // Access KeyCode and KeyMod from the monaco namespace at runtime
      // (cannot import statically as monaco-editor is client-side only)
      const { KeyCode, KeyMod } = monaco

      // Register Run action (Cmd/Ctrl + '). Always register the keybinding so
      // Monaco captures the keystroke (preventing browser defaults); the runtime
      // check on shortcutSettingsRef respects the user's toggle without needing
      // to tear down and re-add the action.
      editorInstance.addAction({
        id: 'beyondctci.runCode',
        label: 'Run Code',
        keybindings: [KeyMod.CtrlCmd | KeyCode.Quote],
        run: () => {
          if (!isSignedInRef.current) return
          if (!shortcutSettingsRef.current.runCode) return
          runHandlerRef.current()
        },
      })

      // Register Submit action (Cmd/Ctrl + Enter)
      editorInstance.addAction({
        id: 'beyondctci.submitCode',
        label: 'Submit Code',
        keybindings: [KeyMod.CtrlCmd | KeyCode.Enter],
        run: () => {
          if (!isSignedInRef.current) return
          if (!shortcutSettingsRef.current.submit) return
          submitHandlerRef.current()
        },
      })
    },
    [handleDidMount],
  )

  // Wrap handleRun to pass test cases from context (discriminated union handles type)
  const handleRunWithTestCases = useCallback(() => {
    if (testCasesState?.type === 'raw') {
      handleRun({ rawTestScripts: testCasesState.scripts })
    } else if (testCasesState?.type === 'class') {
      handleRun({ classTestCases: testCasesState.cases })
    } else if (testCasesState?.type === 'function') {
      handleRun({ testCases: testCasesState.cases })
    } else {
      // No test cases available, run with empty array
      handleRun({ testCases: [] })
    }
  }, [handleRun, testCasesState])

  // Keep refs updated with latest handlers and auth state for keyboard shortcuts.
  useEffect(() => {
    runHandlerRef.current = handleRunWithTestCases
    submitHandlerRef.current = handleSubmit
    isSignedInRef.current = isSignedIn
    shortcutSettingsRef.current = shortcutSettings
  }, [handleRunWithTestCases, handleSubmit, isSignedIn, shortcutSettings])

  // Global keyboard shortcuts so Run/Submit work even when the editor isn't focused
  // (e.g. while reading the problem statement, notes, editorial, etc.).
  // Since our shortcuts always require Cmd/Ctrl they won't conflict with normal
  // typing. We only skip Monaco because it registers its own keybindings above.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (!isSignedInRef.current) return
      // Skip when Monaco editor has focus (it handles its own keybindings)
      if (editorRef.current?.hasTextFocus()) return
      // Skip while a dialog holds focus (e.g. the editor settings modal), so
      // Cmd+Enter inside it cannot submit the code behind it.
      if (
        e.target instanceof Element &&
        e.target.closest('[role="dialog"], [role="alertdialog"]')
      ) {
        return
      }

      const meta = e.metaKey || e.ctrlKey

      // Cmd/Ctrl + ' → Run (use e.code for layout-independent physical key)
      if (meta && e.code === 'Quote' && shortcutSettings.runCode) {
        e.preventDefault()
        runHandlerRef.current()
        return
      }

      // Cmd/Ctrl + Enter → Submit
      if (meta && e.key === 'Enter' && shortcutSettings.submit) {
        e.preventDefault()
        submitHandlerRef.current()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [shortcutSettings.runCode, shortcutSettings.submit, editorRef])

  const setEditorCode = useCallback(
    (value: string) => {
      setCode(value)
    },
    [setCode],
  )

  useEffect(() => {
    registerEditorSetCodeCallback?.(setEditorCode)
    return () => {
      registerEditorSetCodeCallback?.(null)
    }
  }, [registerEditorSetCodeCallback, setEditorCode])

  // Memoize the actions object to prevent re-rendering all context consumers on every render
  const editorActions = useMemo(
    () => ({
      format: handleFormat,
      reset: handleReset,
      setCode: setEditorCode,
      run: handleRunWithTestCases,
      runRaw: handleRunRaw,
      submit: handleSubmit,
      isExecuting,
      isSubmitting,
      isFormatting,
    }),
    [
      handleFormat,
      handleReset,
      setEditorCode,
      handleRunWithTestCases,
      handleRunRaw,
      handleSubmit,
      isExecuting,
      isSubmitting,
      isFormatting,
    ],
  )

  return (
    <section
      aria-label="Code Editor Pane"
      className="pane-card flex h-full min-h-0 flex-col"
      data-pane="code"
    >
      <EditorActionsProvider actions={editorActions}>
        <CodeToolbar
          language={language}
          onLanguageChange={(val: LanguageValue) => setLanguage(val)}
        />
        {vimInitFailed && (
          <div
            role="alert"
            className="border-b border-red-500/30 bg-red-500/10 px-3 py-1.5 text-center text-xs font-medium text-red-600 dark:text-red-400"
          >
            Vim mode unavailable — reload the page to try again.
          </div>
        )}
        <div className="relative min-h-0 flex-1">
          <MonacoLoader
            MonacoComponent={MonacoComponent}
            isClient={isClient}
            monacoProps={{
              height: '100%',
              language,
              value: code,
              theme: isDarkMode ? 'vs-dark' : 'vs',
              onMount: handleDidMountWithShortcuts,
              onChange: handleChangeWithRef,
              options: {
                automaticLayout: true,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: editorSettings.fontSize,
                fontFamily: resolveFontFamily(editorSettings.font),
                fontLigatures: editorSettings.fontLigatures,
                tabSize: editorSettings.tabSize,
                wordWrap: editorSettings.wordWrap ? 'on' : 'off',
                lineNumbers: editorSettings.relativeLineNumbers
                  ? 'relative'
                  : 'on',
                // Mobile optimizations: disable desktop-only features, ensure
                // font ≥ 16px to prevent iOS auto-zoom, disable Monaco's
                // context menu so native iOS cut/copy/paste menu appears.
                // Native selection handles are improved via CSS overrides
                // in styles/editor.css (user-select, touch-action, textarea sizing).
                ...(isMobile && {
                  fontSize: Math.max(16, editorSettings.fontSize),
                  wordWrap: 'on' as const,
                  lineNumbersMinChars: 3,
                  scrollbar: {
                    verticalScrollbarSize: 8,
                    horizontalScrollbarSize: 8,
                  },
                  padding: { top: 8, bottom: 8 },
                  overviewRulerLanes: 0,
                  folding: false,
                  glyphMargin: false,
                  contextmenu: false,
                  quickSuggestions: false,
                  suggestOnTriggerCharacters: false,
                  parameterHints: { enabled: false },
                }),
              },
            }}
          />
        </div>
        <CodeFooter cursorPosition={cursorPosition} />
      </EditorActionsProvider>
    </section>
  )
}
